import { Fragment, useRef, useState } from 'react'

import { TableWrapper } from '@/modules/shared/components/table/TableWrapper'
import useManageTablePreferences from '@/modules/shared/components/table/hooks/useManageTablePreferences'
import { useTableScrollViewportMaxHeight } from '@/modules/shared/components/table/hooks/useTableScrollViewportMaxHeight'
import { TableProps } from '@/modules/shared/components/table/types'
import { classNames } from '@/modules/shared/utils/classNames'

export default function Table<T>({
  tableStyles,
  tableBodyStyles,
  tableHeadStyles,
  tableRowStyles,
  columns,
  columnsHidden,
  renderExtraLine,
  hasExtraLine,
  renderExtraLineSubLine,
  hasExtraLineSubLine,
  keyExtractor,
  dataSource,
  dataTestId,
  pagination,
  hideHeader,
  manageColumns,
  downloadAsCsv,
  resizeColumns = false,
  tableName,
  showBottomPagination = true,
  showTopPagination = true,
  globalManageColumns = false,
  columnPreferenceModalOverlapping = true,
  getSubLines,
  onRowClick,
  isFirst,
  isLast,
  multiRowSelection,
  sortOptions,
  loading,
  selectedRowsFromProps,
  manageRowsPerPage,
  rowsPerPage,
  onRowsPerPageChange,
  constrainScrollToViewport = false,
}: TableProps<T>) {
  const {
    filteredColumns,
    defaultColumns,
    handleUpdateTablePreferences,
    loadingPreferences,
    updatingPreferences,
    savedSortPreferences,
    updateColumnWidth,
  } = useManageTablePreferences({
    tableName,
    columns,
    columnsHidden,
    manageColumns,
    globalManageColumns,
    getSubLines: !!getSubLines,
    sortOptions,
  })

  const [selectedRows, setSelectedRows] = useState<T[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const bottomPaginationRef = useRef<HTMLDivElement>(null)

  const measureScrollViewport = Boolean(
    constrainScrollToViewport &&
    showBottomPagination &&
    !!pagination &&
    (!getSubLines || isLast) &&
    dataSource.length > 0
  )

  const scrollMaxHeightPx = useTableScrollViewportMaxHeight({
    enabled: measureScrollViewport,
    scrollContainerRef,
    bottomPaginationRef,
    marginBelowScrollPx: 20,
  })

  // Avoid flashing on the table
  if (loadingPreferences) return null

  return (
    <div>
      {(!getSubLines || isFirst) && showTopPagination && (
        <TableWrapper.Control
          columnPreferenceModalOverlapping={columnPreferenceModalOverlapping}
          pagination={pagination}
          defaultColumns={defaultColumns}
          manageColumns={manageColumns}
          downloadAsCsv={downloadAsCsv}
          filteredColumns={filteredColumns}
          globalManageColumns={globalManageColumns}
          onPreferencesChange={(filteredColumns, sortOption) =>
            handleUpdateTablePreferences(filteredColumns, sortOption)
          }
          updatingPreferences={updatingPreferences}
          sortOptions={sortOptions}
          savedSortPreferences={savedSortPreferences}
          top
          dataSource={dataSource}
          tableName={tableName}
          manageRowsPerPage={manageRowsPerPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
      {(multiRowSelection?.value || selectedRows).length > 0 && (
        <TableWrapper.MultiRowControl
          multiRowSelection={multiRowSelection}
          selectedRows={multiRowSelection?.value || selectedRows}
          setSelectedRows={multiRowSelection?.setValue || setSelectedRows}
        />
      )}
      <TableWrapper
        ref={scrollContainerRef}
        dataTestId={dataTestId}
        hasData={dataSource.length > 0}
        loading={pagination?.loading || loading}
        className={tableStyles}
        pagination={pagination}
        constrainScrollToViewport={constrainScrollToViewport}
        scrollMaxHeightPx={scrollMaxHeightPx}
      >
        <TableWrapper.Header
          resizeColumns={resizeColumns}
          updateColumnWidth={updateColumnWidth}
          hidden={hideHeader}
          className={tableHeadStyles}
          columns={filteredColumns}
          multiRowSelection={multiRowSelection}
          selectedRows={multiRowSelection?.value || selectedRows}
          setSelectedRows={multiRowSelection?.setValue || setSelectedRows}
          recordsPerPage={dataSource}
        />
        <tbody className={classNames(tableBodyStyles, hideHeader ? 'border' : 'border-x border-b')}>
          {dataSource.map((record, index) => (
            <Fragment key={keyExtractor(record, index)}>
              <TableWrapper.Row
                record={record}
                index={index}
                columns={filteredColumns}
                className={tableRowStyles}
                onRowClick={onRowClick}
                selectedRows={multiRowSelection?.value || selectedRows}
                setSelectedRows={multiRowSelection?.setValue || setSelectedRows}
                multiRowSelection={multiRowSelection}
                hasExtraLine={hasExtraLine}
                getSubLines={getSubLines}
                keyExtractor={keyExtractor}
                selectedRowsFromProps={selectedRowsFromProps}
              />
              {getSubLines &&
                getSubLines(record).map((subLine, index) => (
                  <TableWrapper.SubLine
                    key={subLine.id + 'subLine'}
                    filteredColumns={filteredColumns.filter((column) => !column?.hidden || column?.alwaysVisible)}
                    subLine={subLine}
                    renderExtraLineSubLine={renderExtraLineSubLine}
                    hasExtraLineSubLine={hasExtraLineSubLine}
                    columns={columns}
                    index={index}
                    totalSubLines={getSubLines(record).length}
                    multiRowSelection={!!multiRowSelection}
                  />
                ))}
              {getSubLines && getSubLines(record).length === 0 && (
                <TableWrapper.NoSubLines
                  filteredColumns={filteredColumns.filter((column) => !column?.hidden || column?.alwaysVisible)}
                  multiRowSelection={!!multiRowSelection}
                />
              )}
              {renderExtraLine && hasExtraLine && hasExtraLine(record) && !getSubLines && (
                <tr className={classNames({ 'border-b': index !== dataSource.length - 1 })}>
                  <td colSpan={columns.length + (multiRowSelection ? 1 : 0)} className="space-y-2 px-4 pb-4">
                    {renderExtraLine(record, index)}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </TableWrapper>
      {dataSource.length > 0 && showBottomPagination && pagination && (!getSubLines || isLast) && (
        <div ref={bottomPaginationRef} className="mt-5">
          <TableWrapper.Control
            pagination={pagination}
            tableName={tableName}
            manageRowsPerPage={manageRowsPerPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </div>
      )}
    </div>
  )
}
